import { getFlags } from '@/feature-flags';
import type { SegmentPageEventName, SegmentTrackEvent, SegmentTrackEventName } from '@/types';
import { getSegmentWriteKey } from '@/utils/env';
import { AnalyticsBrowser, type ID, type UserTraits } from '@segment/analytics-next';

const analytics: AnalyticsBrowser | null = loadAnalytics();
const hashedEmails: Record<string, string> = {};

/**
 * Hashes email using SHA-256.
 * Can be moved to 'utils/common' if its needed in other places.
 */
const getHashedEmail = async (email: string): Promise<string> => {
  if (!email) {
    return '';
  }

  const prevHexHash = hashedEmails[email];
  if (prevHexHash) {
    return prevHexHash;
  }

  const normalizedEmail = email.trim().toLowerCase();

  try {
    const hashBuffer = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(normalizedEmail),
    );

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hexHash = hashArray
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();

    hashedEmails[email] = hexHash;
    return hexHash;
  } catch (error) {
    console.error(error);
    return '';
  }
};

function loadAnalytics() {
  const { hasSegmentTrackingAnalytics } = getFlags();
  const writeKey = getSegmentWriteKey();

  try {
    if (hasSegmentTrackingAnalytics && writeKey) {
      return AnalyticsBrowser.load({ writeKey });
    }
  } catch (error) {
    console.error(error);
  }

  return null;
}

async function segmentIdentify(userId: ID, userTraits: UserTraits = {}) {
  try {
    let updatedUserId: ID = userId;
    const updatedTraits: UserTraits = { ...userTraits };

    if (userId) {
      updatedUserId = await getHashedEmail(userId);
    }

    if (userTraits?.email) {
      updatedTraits.email = await getHashedEmail(userTraits.email);
    }

    return await analytics?.identify(updatedUserId, updatedTraits);
  } catch (error) {
    console.error(error);
  }
}

function segmentView<T extends SegmentPageEventName>(pageName: T) {
  try {
    return analytics?.page(pageName);
  } catch (error) {
    console.error(error);
  }
}

async function segmentTrack<T extends SegmentTrackEventName>(
  eventName: T,
  properties?: SegmentTrackEvent[T],
) {
  try {
    let updatedProperties = properties;

    // Hash 'email' field in 'properties'.
    if (properties && 'email' in properties) {
      const email = (properties as { email?: string }).email;

      if (typeof email === 'string') {
        updatedProperties = {
          ...properties,
          email: await getHashedEmail(email),
        } as SegmentTrackEvent[T];
      }
    }
    return await analytics?.track(eventName, updatedProperties);
  } catch (error) {
    console.error(error);
  }
}

export { segmentIdentify, segmentTrack, segmentView };
